import { getBoardImageUrl } from '@/features/board/boardImages';
import { SKYROVER_COLORS } from '@/theme/skyroverTheme';
import type { BoardDefinition } from '@skyrover/shared/types';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

interface BoardSelectDialogProps {
  open: boolean;
  boards: BoardDefinition[];
  selectedBoardId: string;
  onClose: () => void;
  onConfirm: (boardId: string) => void;
}

/** 80% of the original dialog and card sizing */
const DIALOG_MAX_WIDTH_PX = 720;
const BOARD_IMAGE_MAX_WIDTH_PX = 128;
const BOARD_IMAGE_HEIGHT_PX = 80;

export function BoardSelectDialog({
  open,
  boards,
  selectedBoardId,
  onClose,
  onConfirm,
}: BoardSelectDialogProps) {
  const [pendingBoardId, setPendingBoardId] = useState(selectedBoardId);

  useEffect(() => {
    if (open) {
      setPendingBoardId(selectedBoardId);
    }
  }, [open, selectedBoardId]);

  const handleConfirm = () => {
    onConfirm(pendingBoardId);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: `min(${DIALOG_MAX_WIDTH_PX}px, 80vw)`,
          maxWidth: '80%',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          pr: 5,
          py: 1.5,
          fontSize: '1rem',
        }}
      >
        Select Board
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: '#FAFAFA', px: 2, py: 1.5 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' },
            gap: 1.5,
          }}
        >
          {boards.map((board) => {
            const selected = board.id === pendingBoardId;
            return (
              <Box
                key={board.id}
                component="button"
                type="button"
                onClick={() => setPendingBoardId(board.id)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  p: 1.5,
                  border: '2px solid',
                  borderColor: selected ? SKYROVER_COLORS.header : '#E0E0E0',
                  borderRadius: 2,
                  bgcolor: '#fff',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                  boxShadow: selected ? `0 0 0 1px ${SKYROVER_COLORS.header}` : 'none',
                  '&:hover': {
                    borderColor: selected ? SKYROVER_COLORS.header : '#BDBDBD',
                  },
                }}
              >
                <Box
                  component="img"
                  src={getBoardImageUrl(board)}
                  alt={board.displayName}
                  sx={{
                    width: '100%',
                    maxWidth: BOARD_IMAGE_MAX_WIDTH_PX,
                    height: BOARD_IMAGE_HEIGHT_PX,
                    objectFit: 'contain',
                  }}
                />
                <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                  {board.displayName}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                  {board.mcu}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} size="small">
          Close
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleConfirm}
          sx={{
            bgcolor: SKYROVER_COLORS.header,
            '&:hover': { bgcolor: SKYROVER_COLORS.headerHover },
          }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}
